import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { cn } from '@/lib/utils';
import { useResearch } from '../lib/api';
import type { CompanyResearchDto, ResearchVerdict } from '../types';
import { fmtDate } from './_shared';

// Tab — Discovery (papers → companies → candidates). Agent research verdicts on the
// companies behind conference-paper affiliations: mna-paper-research runs one bounded
// Sonnet+web-search call per new name after each weekly conference poll; only CANDIDATE
// verdicts mint review-gated stubs (annotated with the evidence below). Everything here
// is ADVISORY knowledge — revenue figures are agent estimates with the basis stated,
// never confirmed-grade facts. Seeded 2026-07-09 with the full session research.

const VERDICT_CHIPS: { key: string; label: string }[] = [
  { key: 'candidate', label: 'Candidates' },
  { key: 'too_large', label: 'Too large' },
  { key: 'owned_by_major', label: 'Owned by major' },
  { key: 'operator_or_driller', label: 'Operators/drillers' },
  { key: 'services_weak_fit', label: 'Services (weak fit)' },
  { key: 'off_segment', label: 'Off segment' },
  { key: '', label: 'All' },
];

const VERDICT_LABEL: Record<ResearchVerdict, string> = {
  candidate: 'Candidate',
  too_large: 'Too large',
  owned_by_major: 'Owned by major',
  off_segment: 'Off segment',
  operator_or_driller: 'Operator/driller',
  services_weak_fit: 'Services — weak fit',
  unverifiable: 'Unverifiable',
};

export default function Discovery() {
  const [verdict, setVerdict] = useState('candidate');
  const { data, isLoading, error } = useResearch(verdict);

  if (isLoading) return <LoadingSkeleton lines={10} />;
  if (error || !data) return <ErrorState message={(error as Error | null)?.message} />;

  return (
    <div className="stagger">
      <Card>
        <CardTitle>Discovery — companies behind the papers</CardTitle>
        <CardDescription className="mt-2">
          Every author affiliation on high-relevancy conference papers is verified once by a
          research agent (one bounded web-search call, weekly after the conference poll). Only
          candidate verdicts mint review-gated stubs; the rest is published context and the
          agent&apos;s permanent skip-list. Revenue figures are agent estimates — the basis is
          always stated. Nothing here publishes events or facts.
        </CardDescription>
      </Card>

      <Card>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">
            Verdict
          </span>
          {VERDICT_CHIPS.map((c) => (
            <button
              key={c.key || 'all'}
              type="button"
              aria-pressed={verdict === c.key}
              onClick={() => setVerdict(c.key)}
              className={cn(
                'inline-flex items-center rounded-full border px-3 py-1.5',
                'font-(family-name:--font-ui) text-xs font-medium uppercase tracking-[0.12em] transition-colors',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
                verdict === c.key
                  ? 'border-accent bg-surface-elevated text-accent'
                  : 'border-border bg-surface text-muted hover:text-text'
              )}
            >
              {c.label}
            </button>
          ))}
          <span className="ml-auto text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui) [font-variant-numeric:tabular-nums]">
            {data.length} companies
          </span>
        </div>
      </Card>

      <Card>
        {data.length === 0 ? (
          <CardDescription>
            No research rows for this verdict — mna-paper-research (Sun 07:27 UTC, or trigger
            it from Debug) fills this as new papers land.
          </CardDescription>
        ) : (
          <ol className="flex flex-col">
            {data.map((r) => (
              <ResearchRow key={r.id} row={r} />
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}

function scoreTone(score: number): string {
  if (score >= 65) return 'text-score-high';
  if (score >= 45) return 'text-accent';
  return 'text-subtle';
}

function verdictTone(verdict: ResearchVerdict): string {
  if (verdict === 'candidate') return 'border-accent text-accent';
  if (verdict === 'unverifiable') return 'border-border text-subtle';
  return 'border-border text-muted';
}

function ResearchRow({ row: r }: { row: CompanyResearchDto }) {
  const facts = [
    r.hqCountry,
    r.segment && r.segment !== 'off_segment' ? r.segment.replace(/_/g, ' ') : null,
    r.revenueUsdM != null ? `~$${Math.round(r.revenueUsdM)}M (${r.revenueBasis ?? 'est'})` : null,
    r.inBand === true ? 'in band' : r.inBand === false ? 'out of band' : null,
    r.ownership,
  ].filter(Boolean);

  return (
    <li className="flex gap-4 border-b border-border py-3 last:border-0">
      <span
        className={cn(
          'w-12 flex-shrink-0 pt-0.5 text-right font-(family-name:--font-ui) text-lg font-bold [font-variant-numeric:tabular-nums]',
          r.agentScore == null ? 'text-subtle' : scoreTone(r.agentScore)
        )}
        title={`agent candidate-fit score (${r.confidence} confidence)`}
      >
        {r.agentScore == null ? '—' : Math.round(r.agentScore)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-medium text-text">{r.displayName}</span>
          <span
            className={cn(
              'rounded-full border px-2 py-px text-[0.65rem] uppercase tracking-[0.1em] font-(family-name:--font-ui)',
              verdictTone(r.verdict)
            )}
          >
            {VERDICT_LABEL[r.verdict] ?? r.verdict}
          </span>
          <span className="text-[0.65rem] uppercase tracking-[0.1em] text-subtle font-(family-name:--font-ui)">
            {r.confidence} conf · {r.origin} · {fmtDate(r.researchedAt)}
          </span>
          {r.matchedCompanySlug && (
            <Link
              to={`/mna/companies/${r.matchedCompanySlug}`}
              className="rounded-(--radius-sm) border border-accent px-1.5 py-px text-[0.65rem] text-accent hover:bg-surface-elevated font-(family-name:--font-ui)"
            >
              {r.matchedCompanySlug}
            </Link>
          )}
        </div>
        {r.whatItDoes && <p className="mt-0.5 text-sm text-muted">{r.whatItDoes}</p>}
        {facts.length > 0 && (
          <p className="mt-0.5 text-xs text-subtle">{facts.join(' · ')}</p>
        )}
        {r.availabilityAngles && (
          <p className="mt-1 text-xs text-accent">Availability: {r.availabilityAngles}</p>
        )}
        {r.rationale && <p className="mt-1 text-xs italic text-subtle">{r.rationale}</p>}
        {(r.paperDois.length > 0 || r.sourceUrls.length > 0 || r.registryEvidence) && (
          <p className="mt-1 flex flex-wrap items-center gap-1.5">
            {r.paperDois.slice(0, 4).map((doi) => (
              <a
                key={doi}
                href={`https://doi.org/${doi}`}
                target="_blank"
                rel="noreferrer"
                className="rounded-(--radius-sm) bg-surface-elevated px-1.5 py-px text-[0.65rem] text-muted hover:text-text font-(family-name:--font-ui)"
              >
                {doi}
              </a>
            ))}
            {r.sourceUrls.slice(0, 3).map((u) => (
              <a
                key={u}
                href={u}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-0.5 text-[0.65rem] text-accent hover:underline font-(family-name:--font-ui)"
                title={u}
              >
                {new URL(u).hostname.replace(/^www\./, '')}
                <ExternalLink className="h-2.5 w-2.5" aria-hidden />
              </a>
            ))}
            {r.registryEvidence && (
              <span className="text-[0.65rem] text-subtle font-(family-name:--font-ui)">
                {r.registryEvidence}
              </span>
            )}
          </p>
        )}
      </div>
    </li>
  );
}
